import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { UserMenu } from "@/auth/UserMenu";

interface ShellProps {
  children: React.ReactNode;
}

/**
 * Official B2B Negocios brand mark, served as a static asset from
 * `frontend/public/b2b-logo.png`. Vite copies it verbatim into the
 * built bundle root, so the `<img>` resolves against the deployed
 * domain (`https://b2b.panteragpt.com/b2b-logo.png`) without any
 * import-graph hashing.
 *
 * Height stays at 30px to match the previous inline SVG. The header
 * has a `pantera-900` background; the asset already contains the
 * dark navy colors of the logo so we render it as-is.
 */
/**
 * Official B2B Negocios brand mark.
 *
 * The asset (`frontend/public/b2b-logo.png`) is the dark-navy lock-up
 * supplied by the client, designed for light backgrounds. The header
 * has `bg-pantera-900`, so we wrap the logo in a small rounded white
 * card to give it the canvas its design assumes. Vite serves the file
 * straight from the public root — no import hashing — so the
 * deployed URL is `/b2b-logo.png`.
 */
function B2BLogo() {
  return (
    <div className="flex items-center rounded-lg bg-white px-3 py-1.5">
      <img
        src="/b2b-logo.png"
        alt="B2B Negocios"
        className="h-6 w-auto select-none"
        draggable={false}
      />
    </div>
  );
}

export function Shell({ children }: ShellProps) {
  const { t } = useTranslation();

  const navItems = [
    { to: "/", label: t("nav.chat") },
    { to: "/admin/usage", label: t("nav.usage") },
    { to: "/admin/audit", label: t("nav.audit") },
    { to: "/admin/data-sources", label: t("nav.dataSources") },
  ];

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <header className="flex h-16 flex-shrink-0 items-center justify-between border-b border-white/5 bg-pantera-900 px-6">
        <div className="flex items-center gap-5">
          <B2BLogo />
          <div className="h-5 w-px bg-white/10" />
          <nav className="flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-pill px-3 py-1.5 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-white/60 hover:bg-white/5 hover:text-white/90"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <UserMenu />
      </header>
      <main className="flex min-h-0 flex-1 overflow-hidden">{children}</main>
    </div>
  );
}
